import { useEffect, useRef } from 'react';

interface ScreenLockTimeoutOptions {
  enabled: boolean;
  isPipActive?: boolean;
  timeoutSeconds?: number;
  onTimeout: () => void;
}

/**
 * Hook para monitorar quando o dispositivo é bloqueado ou colocado em segundo plano.
 * Se permanecer inativo/bloqueado por mais de `timeoutSeconds` (padrão 60s),
 * encerra a reprodução automaticamente para liberar o fluxo no gerenciador.
 * 
 * Se o modo Picture-in-Picture (PiP) estiver ativo, o timeout é desativado
 * para permitir que o usuário continue assistindo em janela flutuante.
 */
export function useScreenLockTimeout({
  enabled,
  isPipActive = false,
  timeoutSeconds = 60,
  onTimeout,
}: ScreenLockTimeoutOptions) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  useEffect(() => {
    if (!enabled) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    const clearTimer = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const startTimer = () => {
      // Se Picture-in-Picture estiver ativo, a aba pode ficar 'hidden' intencionalmente
      if (isPipActive) {
        return;
      }
      clearTimer();
      timeoutRef.current = setTimeout(() => {
        console.log(`[ScreenLockTimeout] Dispositivo inativo/bloqueado por ${timeoutSeconds}s. Encerrando sessão de reprodução.`);
        onTimeoutRef.current();
      }, timeoutSeconds * 1000);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        startTimer();
      } else {
        clearTimer();
      }
    };

    const handlePageHide = () => {
      startTimer();
    };

    // Inicialização se já estiver oculto
    if (document.visibilityState === 'hidden') {
      startTimer();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      clearTimer();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [enabled, isPipActive, timeoutSeconds]);
}
